import { z } from "zod";

const nullableTimestampSchema = z.string().datetime({ offset: true }).nullable();

export const nativeHealthCapabilitySchema = z.object({
  schemaVersion: z.literal(1),
  enabled: z.boolean(),
  available: z.boolean(),
  platform: z.enum(["android", "ios", "unknown"]),
  provider: z.enum(["health_connect", "healthkit", "unavailable"]),
  supportsHistory: z.boolean(),
  supportsBackground: z.boolean()
});

export const nativeHealthMetricValueSchema = z.object({
  value: z.number().nullable(),
  unit: z.string().nullable(),
  measuredAt: nullableTimestampSchema.optional(),
  periodStart: nullableTimestampSchema.optional()
});

export const nativeHealthSectionSchema = z.object({
  key: z.string().min(1),
  title: z.string().min(1),
  metrics: z.array(
    z.object({
      key: z.string().min(1),
      label: z.string().min(1),
      value: nativeHealthMetricValueSchema
    })
  )
});

export const nativeHealthWorkoutSchema = z.object({
  id: z.string().min(1),
  activityType: z.string(),
  startedAt: z.string().datetime({ offset: true }),
  endedAt: z.string().datetime({ offset: true }),
  energy: z.number().nullable().optional()
});

export const nativeHealthStateSchema = z.object({
  schemaVersion: z.literal(1),
  connectorId: z.string().min(1).nullable(),
  status: z.enum([
    "unavailable",
    "disconnected",
    "denied",
    "partial",
    "syncing",
    "connected",
    "paused",
    "recovery_required",
    "error"
  ]),
  authorization: z.enum(["not_determined", "denied", "partial", "authorized"]),
  coverage: z.array(z.string()),
  deniedMetrics: z.array(z.string()),
  sync: z.object({
    phase: z.enum(["idle", "reading", "uploading", "reconciling", "paused"]),
    progress: z.number().min(0).max(100).nullable(),
    pendingEvents: z.number().int().nonnegative(),
    lastSyncedAt: nullableTimestampSchema,
    lastErrorCode: z.string().nullable()
  }),
  summary: z
    .object({
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      steps: nativeHealthMetricValueSchema.nullable(),
      distance: nativeHealthMetricValueSchema.nullable(),
      activeEnergy: nativeHealthMetricValueSchema.nullable(),
      weight: nativeHealthMetricValueSchema.nullable(),
      heartRate: nativeHealthMetricValueSchema.nullable(),
      restingHeartRate: nativeHealthMetricValueSchema.nullable(),
      oxygenSaturation: nativeHealthMetricValueSchema.nullable(),
      sleepMinutes: nativeHealthMetricValueSchema.nullable(),
      workouts: z.array(nativeHealthWorkoutSchema),
      dailyHistory: z
        .array(
          z.object({
            date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            steps: z.number().nonnegative().nullable()
          })
        )
        .optional(),
      sections: z.array(nativeHealthSectionSchema).optional()
    })
    .nullable()
});

const ownerPayloadSchema = z.object({ ownerKey: z.string().min(1) });

export const nativeHealthCommandSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("health:getCapability") }),
  z.object({ type: z.literal("health:getState"), payload: ownerPayloadSchema }),
  z.object({ type: z.literal("health:requestAuthorization"), payload: ownerPayloadSchema }),
  z.object({
    type: z.literal("health:syncNow"),
    payload: ownerPayloadSchema.extend({ reason: z.enum(["route_open", "manual"]) })
  }),
  z.object({ type: z.literal("health:openSettings") }),
  z.object({ type: z.literal("health:disconnect"), payload: ownerPayloadSchema })
]);

export const nativeHealthActionAckSchema = z.object({ ok: z.boolean() });

export type NativeHealthCapability = z.infer<typeof nativeHealthCapabilitySchema>;
export type NativeHealthMetricValue = z.infer<typeof nativeHealthMetricValueSchema>;
export type NativeHealthSection = z.infer<typeof nativeHealthSectionSchema>;
export type NativeHealthState = z.infer<typeof nativeHealthStateSchema>;
export type NativeHealthCommand = z.infer<typeof nativeHealthCommandSchema>;
export type NativeHealthActionAck = z.infer<typeof nativeHealthActionAckSchema>;

export const hasNativeHealthMetricValue = (
  metric: NativeHealthMetricValue | null | undefined
): metric is NativeHealthMetricValue => metric?.value !== null && metric?.value !== undefined;

const parseUnknownJson = (value: unknown): unknown => (typeof value === "string" ? JSON.parse(value) : value);

export const parseNativeHealthCapability = (value: unknown): NativeHealthCapability =>
  nativeHealthCapabilitySchema.parse(parseUnknownJson(value));

export const parseNativeHealthState = (value: unknown): NativeHealthState =>
  nativeHealthStateSchema.parse(parseUnknownJson(value));

export const parseNativeHealthActionAck = (value: unknown): NativeHealthActionAck =>
  nativeHealthActionAckSchema.parse(parseUnknownJson(value));
